import { useMemo } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import {
	createFileRoute,
	Link,
	Outlet,
	useRouteContext,
} from '@tanstack/react-router'
import { isEmpty } from 'lodash/fp'
import {
	ArrowLeftIcon,
	CircleDollarSignIcon,
	GlobeXIcon,
	ListCollapseIcon,
	Settings2Icon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { columnsQueries } from '@/entities/column'
import { optimizationGroupQueries } from '@/entities/optimization-group'

import { OptimizationGroupSwitcher } from '@/widgets/optimization-group-switcher'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	PageNavigatorHost,
	PageNavigatorProvider,
} from '@/shared/components/PageNavigator'
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { Button } from '@/shared/components/ui/button'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Header, Layout } from '@/shared/components/ui/layout'
import { Separator } from '@/shared/components/ui/separator'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { HiddenOnContext } from '@/shared/generated'
import { useCoreStore } from '@/shared/lib/core-store'
import { DatasheetProvider } from '@/shared/lib/datasheet/context'
import { useProductsTableConfigStore } from '@/shared/lib/table-config'
import { getInitialTableState } from '@/shared/tools/columns'

function OptimizationGroupHost() {
	const { t } = useTranslation()

	const { optimizationGroupId } = useRouteContext({
		from: '/_app/p/og/$optimizationGroupId',
	})
	const storedUserConfigs = useProductsTableConfigStore((state) => ({
		columnVisibility: state.columnVisibility,
		columnPinning: state.columnPinning,
		columnOrder: state.columnOrder,
		columnSizing: state.columnSizing,
	}))
	const { data: schema } = useSuspenseQuery(
		columnsQueries.list(HiddenOnContext.Products)
	)

	const initialState = useMemo(() => {
		const defaultPreset = getInitialTableState(schema)

		if (!isEmpty(storedUserConfigs)) {
			return {
				...storedUserConfigs,
				defaultPreset,
				schema,
				rawSchema: schema,
			}
		} else {
			return {
				...defaultPreset,
				defaultPreset,
				schema,
				rawSchema: schema,
			}
		}
	}, [storedUserConfigs, schema])

	return (
		<DatasheetProvider
			key={optimizationGroupId}
			{...initialState}
			datasheetId='optimization-group-products'
		>
			<PageNavigatorProvider>
				<ErrorBoundary fallback={() => <NotFound />}>
					<Layout>
						<Header className='h-11 items-center border-b px-4 py-2'>
							<CircleDollarSignIcon className='size-4' />
							<Separator orientation='vertical' className='mx-2 h-5' />
							<Breadcrumb>
								<BreadcrumbList>
									<BreadcrumbItem>
										<BreadcrumbLink asChild>
											<Link
												to='/p/og'
												data-test-id='breadcrumb-link'
												className='max-w-32 truncate'
											>
												{t('menu.og', { defaultValue: 'Optimization groups' })}
											</Link>
										</BreadcrumbLink>
									</BreadcrumbItem>
									<BreadcrumbSeparator />
									<BreadcrumbItem>
										<BreadcrumbPage>
											<OptimizationGroupSwitcher />
										</BreadcrumbPage>
									</BreadcrumbItem>
								</BreadcrumbList>
							</Breadcrumb>
							<Separator orientation='vertical' className='mx-2 h-5' />
							<div className='flex items-center gap-1'>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button size='icon' variant='ghost' asChild>
											<Link
												to='/p/og/$optimizationGroupId/products'
												params={{ optimizationGroupId }}
												className='data-[status=active]:bg-muted dark:data-[status=active]:bg-muted'
											>
												<ListCollapseIcon className='size-4' />
											</Link>
										</Button>
									</TooltipTrigger>
									<TooltipContent>{t('app.products')}</TooltipContent>
								</Tooltip>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button size='icon' variant='ghost' asChild>
											<Link
												to='/p/og/$optimizationGroupId/settings'
												params={{ optimizationGroupId }}
												className='data-[status=active]:bg-muted dark:data-[status=active]:bg-muted'
											>
												<Settings2Icon className='size-4' />
											</Link>
										</Button>
									</TooltipTrigger>
									<TooltipContent>{t('settings')}</TooltipContent>
								</Tooltip>
							</div>
							<div className='ml-auto'>
								<PageNavigatorHost />
							</div>
						</Header>
						<Outlet />
					</Layout>
				</ErrorBoundary>
			</PageNavigatorProvider>
		</DatasheetProvider>
	)
}
const LoadingState = () => (
	<Empty>
		<EmptyHeader>
			<EmptyMedia variant='icon'>
				<Spinner data-testid='og-loading-spinner' />
			</EmptyMedia>
		</EmptyHeader>
	</Empty>
)
const NotFound = () => {
	const { t } = useTranslation()

	return (
		<Empty>
			<EmptyHeader>
				<EmptyMedia variant='icon'>
					<GlobeXIcon />
				</EmptyMedia>
				<EmptyTitle>
					{t('error.og.not_found.title', {
						defaultValue: 'Can`t get optimization group',
					})}
				</EmptyTitle>
				<EmptyDescription>
					{t('error.og.not_found.subtitle', {
						defaultValue:
							'It could heppens becasue of permissions or network issues',
					})}
				</EmptyDescription>
				<EmptyContent>
					<div className='rounded-md bg-muted/30 p-2 text-left'>
						<ul className='list-inside list-disc space-y-2 text-xs text-muted-foreground'>
							<li>
								{t('error.og.not_found.sol_1', {
									defaultValue: 'Check your permissions.',
								})}
							</li>
							<li>
								{t('error.og.not_found.sol_2', {
									defaultValue: 'Ensure that this optimzation group exists.',
								})}
							</li>
							<li>
								{t('error.og.not_found.sol_3', {
									defaultValue: 'Please contact your administrator.',
								})}
							</li>
						</ul>
					</div>
					<Button asChild variant='link'>
						<Link to='/p/og'>
							<ArrowLeftIcon />
							{t('menu.og', { defaultValue: 'Optimization groups' })}
						</Link>
					</Button>
				</EmptyContent>
			</EmptyHeader>
		</Empty>
	)
}
export const Route = createFileRoute('/_app/p/og/$optimizationGroupId')({
	pendingComponent: LoadingState,
	errorComponent: NotFound,
	beforeLoad: async ({ context, params }) => {
		await context.queryClient.ensureQueryData(
			optimizationGroupQueries.single(params.optimizationGroupId)
		)
		const optimizationGroupId = params.optimizationGroupId
		useCoreStore.getState().setOptimizationGroupId(optimizationGroupId)

		return { optimizationGroupId }
	},
	component: OptimizationGroupHost,
})
